import {
  useRef,
  useState,
  useEffect,
  useContext,
  useCallback,
  useMemo,
} from "react";
import { useNavigate, useLocation } from "react-router-dom";
import useRetryFetch from "../hooks/useRetryFetch";
import useProcess from "../hooks/useProcess";
import { useTranslation } from "react-i18next";
import {
  ReactFlow,
  addEdge,
  Background,
  ConnectionLineType,
  MarkerType,
  useStoreApi,
  useReactFlow,
  useNodesInitialized,
  Connection,
  Edge,
  EdgeChange,
  BackgroundVariant,
} from "@xyflow/react";
import Activity from "./reactflow/Activity";
import CustomEdge from "./reactflow/CustomEdge";
import { MyContext } from "./reactflow/MyContextProvider";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  solid /* regular */,
} from "@fortawesome/fontawesome-svg-core/import.macro"; // <-- import styles to be used
import { directionService } from "../services/directionService";

import "@xyflow/react/dist/style.css";

import "./DesignView.css";
import DesignMenu from "./DesignMenu";
import MessageBox from "./MessageBox";
import { Button } from "./ui/button";
import ComboBox from "./ui/combobox";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "./ui/select";
import { useEffectOnce } from "../hooks/useEffectOnce";
import { config } from "../Environment";

import useAuth from "../hooks/useAuth";
import customhistory from "../history/customhistory";
import EditActivity from "./EditActivity";
import LoggerContext from "../context/LoggerProvider";
import validatePrescription from "./reactflow/validate_prescription";
import { convertOldCustomToNew, LANGUAGES } from "../lib/utils";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "./ui/dialog";
import { LocationState } from "../lib/types";
// for modal version of Preview!
import Preview from "./Preview";

import { toast } from "sonner";
import { ArrowLeft, ArrowRight } from "lucide-react";

import WorkflowPermissionDialog from "./WorkflowPermissionDialog";

const HIGHLIGHT_TYPE = {
  NONE: 0,
  ACTIVITY: 1,
  EDGE: 2,
};

const DesignView = () => {
  const {
    nodes,
    edges,
    data,
    onNodesChange,
    onEdgesChange,
    setNodes,
    setEdges,
    setData,
    showModal,
    setShowModal,
    showDataItemMapping,
    setShowDataItemMapping,
    setDataItemMapping,
    dataItemMapping,
    mappingNodeId,
    setMappingNodeId,
    selectedOption,
    setSelectedOption,
    selectedActivity,
    setSelectedActivity,
    showOptionEdit,
    setShowOptionEdit,
    showRemoveActivity,
    setShowRemoveActivity,
    designDirection,
    setDesignDirection,
    labelBeingEdited,
    setToBeSaved,
    transpose,
    prescriptionLanguage,
    showOptionI18N,
    setShowOptionI18N,
    showActivityEdit,
    setShowActivityEdit,
    setPrescription,
    clearReactFlow,
    flowContext,
    setFlowContext,
    selectableContext,
    setSelectableContext,
    contract,
    setContract,
    generateFlowcontextFromData,
    selectedMapping,
    setSelectedMapping,
    paneWasClicked,
    setPaneWasClicked,
  } = useContext(MyContext);

  const { Logger } = useContext(LoggerContext);

  const unsavedChangesRef = useRef(false);

  const retryFetch = useRetryFetch();
  const { startProcess } = useProcess();
  const location = useLocation();
  const navigate = useNavigate();

  /**
   * The `setSearchParams` function from `useSearchParams` causes a navigation,
   * so we use the `replaceState` function from the browser's History API instead.
   * @param patch object with key-value pairs to set or update in the query string
   */
  function updateSearchParams(patch: object) {
    /*
    setSearchParams((current) => {
      Object.entries(patch).forEach(([key, value]) => {
        current.set(key, value);
      });
      return current;
    });
    */
    const browserSearchParams = new URLSearchParams(window.location.search);
    Object.entries(patch).forEach(([key, value]) => {
      browserSearchParams.set(key, value);
    });
    window.history.replaceState({}, "", "?" + browserSearchParams.toString());
  }

  // background shows that PREVIEW should be opened as modal on top of current page!
  let background = null;
  if (location.state && typeof location.state === "object") {
    const state = location.state as LocationState;
    if (state.background && typeof state.background === "object") {
      if ("pathname" in state.background) {
        background =
          state.background.pathname !== "/" ? state.background : null;
      }
    }
  }

  const { auth } = useAuth();

  const { t } = useTranslation();

  const [presName, setPresName] = useState("");
  const [initialPresName, setInitialPresName] = useState("");
  const [presOwner, setPresOwner] = useState("");
  const [currentPrescriptionId, setCurrentPrescriptionId] = useState("");
  const [saveIntent, setSaveIntent] = useState(false);
  const [saveIntentAssignMessage, setSaveIntentAssignMessage] = useState(false);
  const [saveBeforeNewIntent, setSaveBeforeNewIntent] = useState("");
  const [unsavedChanges, setUnsavedChanges] = useState(false);

  const [showMessageBox, setShowMessageBox] = useState(false);
  const [msgBoxTitle, setMsgBoxTitle] = useState("");
  const [msgBoxButtons, setMsgBoxButtons] = useState({});
  const [msgBoxInputs, setMsgBoxInputs] = useState<any[]>([]);
  const [msgBoxId, setMsgBoxId] = useState("");
  const [msgBoxMoreJSX, setMsgBoxMoreJSX] = useState<React.ReactNode | null>(
    null
  );

  const [showAddNested, setShowAddNested] = useState(false);
  const [showNewPrescriptionSave, setShowNewPrescriptionSave] = useState(false);
  const [presNameNew, setPresNameNew] = useState("");
  const [showNewPrescription, setShowNewPrescription] = useState(false);
  const [showSavePrescriptionAs, setShowSavePrescriptionAs] = useState(false);
  const [showNotOwnerSaveCopy, setShowNotOwnerSaveCopy] = useState(false);
  const [showLoginRegister, setShowLoginRegister] = useState(false);

  const [isLoading, setLoading] = useState(false);

  const [currentOption, setCurrentOption] = useState<any>({});
  const [currentActivity, setCurrentActivity] = useState<any>({});
  const [nodesDueSelecting, setNodesDueSelecting] = useState(false);
  const [nodesDueUnSelecting, setNodesDueUnSelecting] = useState(false);

  const [transposable, setTransposable] = useState(true);

  const [selectedNestable, setSelectedNestable] = useState({});
  const [isComboboxValid, setIsComboboxValid] = useState(false);

  const [showPermissionDialog, setShowPermissionDialog] = useState(false);

  const store = useStoreApi();
  const flow = useReactFlow();
  const nodesInitialized = useNodesInitialized({
    includeHiddenNodes: false,
  });

  const highlightInfoRef = useRef({ type: HIGHLIGHT_TYPE.NONE, id: "" });

  const nodeTypes = useMemo(() => ({ activity: Activity }), []);
  const edgeTypes = useMemo(
    () => ({
      customedge: CustomEdge,
    }),
    []
  );

  const detectedUnsavedChanges = () => {
    setUnsavedChanges(true);
  };
  const onConnect = useCallback(
    (params: Connection) => {
      detectedUnsavedChanges();
      return setEdges((eds: Edge[]) => {
        Logger.debug("Connect params", params, eds);
        const newParams: Edge = {
          id: `${params.source}-${params.target}`,
          ...params,
          source: params.source || "",
          target: params.target || "",
          markerEnd: {
            type: MarkerType.ArrowClosed,
            color: "black",
            width: 20,
            height: 20,
          },
          type: "customedge",
        };
        return addEdge(newParams, eds);
      });
    },
    [setEdges]
  );

  const openMessageBox = (
    title: string,
    id: string,
    buttons: Record<string, () => void> | { id: string; text: string }[],
    inputs: Array<any>,
    moreJSX: React.ReactNode
  ) => {
    setMsgBoxTitle(title);
    setMsgBoxId(id);
    setMsgBoxButtons(buttons);
    setMsgBoxInputs(inputs);
    setMsgBoxMoreJSX(moreJSX);
    setShowMessageBox(true);
  };

  const handleRemoveOptionOk = (nodeId: string, idx: number) => {
    setData((prevData: Record<string, any>) => {
      const cloned = { ...prevData };
      cloned[nodeId] = { ...cloned[nodeId] };
      cloned[nodeId].options.splice(idx, 1);
      return cloned;
    });

    // remove edge
    setEdges((prevEdges: Edge[]) => {
      const cloned = [...prevEdges];
      const handleId = `${nodeId}-${idx}`;
      const edgeToRemoveIdx = cloned.findIndex(
        (edge) => edge.sourceHandle === handleId
      );
      cloned.splice(edgeToRemoveIdx, 1);
      return cloned;
    });

    highlightInfoRef.current = {
      ...highlightInfoRef.current,
      type: HIGHLIGHT_TYPE.NONE,
    };
  };

  const getEdgesForNode = (nodeId: string): Edge[] => {
    const result: Edge[] = [];
    for (const edge of edges) {
      if (edge.source === nodeId || edge.target === nodeId) {
        result.push({ ...edge });
      }
    }
    return result;
  };

  interface FlowContextEntry {
    key: string;
    description: string;
  }

  interface ContractVariable {
    value: string;
    label: string;
  }

  const updateContract = (newFlowContext: FlowContextEntry[]) => {
    const newContract = structuredClone(contract);

    for (let i = newContract.length - 1; i >= 0; i--) {
      const contractVariable = newContract[i] as ContractVariable;
      const foundVariable = newFlowContext.find(
        (entry) => entry.key === contractVariable.value
      );
      if (!foundVariable) {
        newContract.splice(i, 1);
      } else {
        contractVariable.label = foundVariable.description;
      }
    }

    setContract(newContract);
  };

  const handleRemoveActivityOk = async () => {
    const edgesToRemove = getEdgesForNode(highlightInfoRef.current.id);
    const changes: EdgeChange[] = edgesToRemove.map((edge) => ({
      type: "remove",
      id: edge.id,
    }));
    onEdgesChange(changes);

    const idToRemoveFromData = highlightInfoRef.current.id;
    // recreate flow context
    const result: FlowContextEntry[] = [];
    await generateFlowcontextFromData(data, result, "", idToRemoveFromData);
    setFlowContext(result);
    updateContract(result);

    onNodesChange([{ type: "remove", id: highlightInfoRef.current.id }]);

    highlightInfoRef.current = {
      ...highlightInfoRef.current,
      type: HIGHLIGHT_TYPE.NONE,
    };
  };

  const handleSaveOption = (nodeId: string, idx: number) => {
    setData((prevData: Record<string, any>) => {
      const clonedData = { ...prevData };
      const clonedOption = { ...clonedData[nodeId].options[idx] };
      clonedOption.text = currentOption.text;
      clonedOption.value = currentOption.value;
      clonedData[nodeId].options[idx] = clonedOption;
      return clonedData;
    });
  };

  const handleLabelTextInput = (e: React.ChangeEvent<HTMLInputElement>) => {
    setCurrentOption((prevOption: any) => ({
      ...prevOption,
      text: {
        ...prevOption.text,
        [prescriptionLanguage]: e.target.value,
      },
    }));
    setUnsavedChanges(true);
  };

  const addNewActivity = () => {
    const {
      height,
      width,
      transform: [transformX, transformY, zoomLevel],
    } = store.getState();

    Logger.debug("Design view dimensions:", {
      width,
      height,
      transformX,
      transformY,
      zoomLevel,
    });

    let newX =
      (Math.floor(Math.random() * (width - 250)) - transformX) / zoomLevel;
    let newY =
      (Math.floor(Math.random() * (height - 100)) - transformY) / zoomLevel;

    let newNodeId = Math.floor(Math.random() * 10000).toString();

    let newNodeType = "default";
    if (!nodes.length) {
      newNodeType = "start";
    }

    setNodes((prevNodes: any[]) => {
      let cloned = [...prevNodes];
      cloned.push({
        id: newNodeId,
        position: {
          x: newX,
          y: newY,
        },
        type: "activity",
      });
      return cloned;
    });

    setData((prevData: Record<string, any>) => {
      let cloned = { ...prevData };
      cloned[newNodeId] = {
        label: {
          en: "",
          de: "",
          he: "",
        },
        type: newNodeType,
        options: [
          {
            text: {
              en: "",
              de: "",
              he: "",
            },
            value: `option${Math.floor(Math.random() * 10000)}`,
            handle: `${newNodeId}-0`,
          },
        ],
      };
      return cloned;
    });

    setUnsavedChanges(true);
  };

  const addNestedPrescription = async (nestedPresObj: any) => {
    Logger.debug("Adding nested prescription from", nestedPresObj);

    const {
      height,
      width,
      transform: [transformX, transformY, zoomLevel],
    } = store.getState();
    Logger.debug(width, height, transformX, transformY, zoomLevel);
    let newX =
      (Math.floor(Math.random() * (width - 250)) - transformX) / zoomLevel;
    let newY =
      (Math.floor(Math.random() * (height - 100)) - transformY) / zoomLevel;
    let newNodeId = Math.floor(Math.random() * 10000).toString();

    // determine options from openExits
    let diagramObj = JSON.parse(nestedPresObj.diagramJSON);
    let exitOptions: any[] = [];
    let optIdx = 0;
    for (let openExit of diagramObj.openExits) {
      exitOptions.push({
        text: {
          [prescriptionLanguage]: `${openExit.nodeLabel} [${openExit.optionLabel}]`,
        },
        value: openExit.optionValue || "?????",
        innerHandle: openExit.handleId,
        handle: `${newNodeId}-${optIdx}`,
      });
      optIdx++;
    }

    // if there is no current history id, it means that prescription is dirty and new history entry needs to be created
    let presHistoryId = nestedPresObj.history;
    if (!presHistoryId) {
      Logger.debug(
        "Need to create a new version of this prescription while nesting it"
      );
      let response = await retryFetch(
        `${config.API_BASE}/api/prescriptions/newversion/${nestedPresObj._id}`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          credentials: "include",
          body: "{}",
        }
      );
      let responseJSON = await response.json();

      presHistoryId = responseJSON.newHistoryId;
    }

    setNodes((prevNodes: any[]) => {
      let cloned = [...prevNodes];
      cloned.push({
        id: newNodeId,
        position: {
          x: newX,
          y: newY,
        },
        type: "activity",
      });
      return cloned;
    });
    setData((prevData: Record<string, any>) => {
      let cloned = { ...prevData };
      cloned[newNodeId] = {
        label: {
          en: `${nestedPresObj.name}`,
          de: `${nestedPresObj.name}`,
          he: `${nestedPresObj.name}`,
        },
        type: "prescription",
        historyId: presHistoryId,
        dataitemmapping: diagramObj.contract?.length
          ? diagramObj.contract.map((variable: any) => {
              return {
                key: variable.value,
                description: variable.label,
                type: variable.type || "text",
                mappedToOuter: { key: "", description: "" },
              };
            })
          : [],
        options: exitOptions,
      };
      return cloned;
    });

    // add nested flowcontext to current context
    if (diagramObj.flowcontext) {
      setFlowContext((oldFlowContext: any) => {
        let newFlowContext = structuredClone(oldFlowContext);
        for (let newEntry of diagramObj.flowcontext) {
          // correct description to include nested prescription name
          newEntry.description = `${newEntry.description} (${nestedPresObj.name})`;
          newFlowContext.push(newEntry);
        }
        return newFlowContext;
      });
    } else {
      let newFlowContext = structuredClone(flowContext);
      await generateFlowcontextFromData(
        diagramObj.data,
        newFlowContext,
        nestedPresObj.name
      );
      setFlowContext(newFlowContext);
    }

    setUnsavedChanges(true);
  };

  const runPrescription = async (prescriptionId: string) => {
    try {
      await startProcess({
        prescriptionId,
        from: location,
        forward: location,
      });
    } catch (error) {
      console.error("Error running prescription:", error);
      toast.error("An error occurred while starting the prescription.");
    }
  };

  useEffect(() => {
    if (
      nodesInitialized &&
      transposable &&
      designDirection !==
        LANGUAGES[prescriptionLanguage as keyof typeof LANGUAGES].dir
    ) {
      initiateTranspose();
    }
  }, [nodesInitialized]);

  useEffect(() => {
    if (currentPrescriptionId) {
      console.log(`updateSearchParams set wid ${currentPrescriptionId} (typeof ${typeof currentPrescriptionId})`);
      updateSearchParams({ wid: currentPrescriptionId ?? "" });
      sessionStorage.setItem("prescriptionID", currentPrescriptionId);
    }
  }, [currentPrescriptionId]);

  useEffect(() => {
    if (showOptionEdit && selectedOption.nodeId) {
      Logger.debug("EDIT required for option", selectedOption);

      let theOption = {
        ...data[selectedOption.nodeId].options[selectedOption.idx],
      };
      Logger.debug("It's the option", theOption);
      setCurrentOption(theOption);
    }
  }, [showOptionEdit, selectedOption]);

  useEffect(() => {
    if (showActivityEdit && selectedActivity) {
      Logger.debug("EDIT required for activity", selectedActivity);

      let theActivity = {
        ...data[selectedActivity],
      };
      Logger.debug("It's the activity", theActivity);
      setCurrentActivity(theActivity);
    }
  }, [showActivityEdit, selectedActivity]);

  const changeHighlightInfo = (
    id: string,
    elementtype: number,
    selectIt: boolean
  ) => {
    Logger.debug(
      "Change highlight type",
      id,
      highlightInfoRef.current.id,
      elementtype,
      highlightInfoRef.current.type,
      selectIt
    );
    if (elementtype === HIGHLIGHT_TYPE.NONE) {
      //setHighlightInfo({ type: HIGHLIGHT_TYPE.NONE });
      highlightInfoRef.current = {
        ...highlightInfoRef.current,
        type: HIGHLIGHT_TYPE.NONE,
      };
    } else {
      if (
        !selectIt &&
        elementtype === highlightInfoRef.current.type &&
        id === highlightInfoRef.current.id
      ) {
        // unselect only if type is correct and id matches
        //setHighlightInfo({ type: HIGHLIGHT_TYPE.NONE });
        highlightInfoRef.current = {
          ...highlightInfoRef.current,
          type: HIGHLIGHT_TYPE.NONE,
        };
      } else if (selectIt) {
        //setHighlightInfo({ type: elementtype, id });
        highlightInfoRef.current = { type: elementtype, id };
      }
    }
    Logger.debug("new ref", highlightInfoRef.current);
  };

  const handleNodesChange = (changes: any) => {
    Logger.debug("Nodes changes", changes);
    let resultchanges = [];
    for (let change of changes) {
      // only handle select types
      if (change.type === "select") {
        changeHighlightInfo(
          change.id,
          HIGHLIGHT_TYPE.ACTIVITY,
          change.selected
        );
        if (!change.selected && labelBeingEdited) {
          setToBeSaved(true);
        }
      }
      if (change.type !== "remove") {
        resultchanges.push(change);
      } else {
        setShowRemoveActivity(true);
      }
    }
    onNodesChange(resultchanges);
  };

  const handleEdgesChange = (changes: any) => {
    Logger.debug("Edges changes", changes);
    let resultChanges = [];
    for (let change of changes) {
      if (change.type === "select") {
        changeHighlightInfo(change.id, HIGHLIGHT_TYPE.EDGE, change.selected);
        resultChanges.push(change);
      } else if (
        change.type !== "remove" ||
        highlightInfoRef.current.type === HIGHLIGHT_TYPE.EDGE
      ) {
        resultChanges.push(change);
        highlightInfoRef.current = {
          ...highlightInfoRef.current,
          type: HIGHLIGHT_TYPE.NONE,
        };
      }
    }
    onEdgesChange(resultChanges);
  };

  useEffect(() => {
    unsavedChangesRef.current = unsavedChanges;

    let unblock: any;
    if (unsavedChanges) {
      // Block navigation and register a callback that
      // fires when a navigation attempt is blocked.
      unblock = customhistory.block((tx) => {
        // Navigation was blocked! Let's show a confirmation dialog
        // so the user can decide if they actually want to navigate
        // away and discard changes they've made in the current page.
        if (window.confirm(t("question_unsaved_changes_when_leaving"))) {
          // Unblock the navigation.
          unblock();

          // Retry the transition.
          tx.retry();
        }
      });
    }

    return () => {
      if (typeof unblock === "function") {
        unblock();
      }
    };
  }, [unsavedChanges]);

  useEffect(() => {
    if (saveIntent) {
      setSaveIntent(false);
      // call save
      callMenu("save");
    }
  }, [saveIntent]);

  useEffect(() => {
    if (saveBeforeNewIntent) {
      // call save
      callMenu("save");

      // new
      sessionStorage.removeItem("prescriptionID");
      setPresName(saveBeforeNewIntent);
      setInitialPresName(saveBeforeNewIntent);

      clearReactFlow();
      setSaveBeforeNewIntent("");
    }
  }, [saveBeforeNewIntent]);

  const removeAbandonedNodeData = (tempNodes: any, tempData: any) => {
    let result: any = {};

    for (let node of tempNodes) {
      result[node.id] = tempData[node.id];
    }

    return result;
  };

  const generateDiagram = () => {
    let cleanedNodes = structuredClone(nodes);
    if (
      designDirection !==
      LANGUAGES[prescriptionLanguage as keyof typeof LANGUAGES].dir
    ) {
      if (cleanedNodes.length)
        Logger.debug("TRANSPOSE called from generateDiagram");
      cleanedNodes = transpose(cleanedNodes);
    }

    cleanedNodes = removeExtraProperties(cleanedNodes);

    let cleanedData = structuredClone(data);
    cleanedData = removeAbandonedNodeData(cleanedNodes, cleanedData);

    for (let nodeId of Object.keys(cleanedData)) {
      let nodeData = cleanedData[nodeId];
      if (!!nodeData.custom && !Array.isArray(nodeData.custom)) {
        nodeData.custom = convertOldCustomToNew(nodeData.custom);
      }
    }

    let openExits = findOpenExitHandles();

    let diagram = {
      prescriptionLanguage,
      flowcontext: flowContext,
      contract: contract,
      version: 2,
      nodes: cleanedNodes,
      edges,
      data: cleanedData,
      openExits,
    };
    return diagram;
  };

  const saveDraft = () => {
    let diagram = JSON.stringify(generateDiagram());
    sessionStorage.setItem("draftprescription", diagram);
  };

  const removeDraft = () => {
    sessionStorage.removeItem("draftprescription");
  };

  const callMenu = (menuid: string) => {
    const pseudoEvent = {
      target: {
        dataset: {
          id: menuid,
        },
      },
    };
    onMenuClick(pseudoEvent);
  };

  const handleChange = (nestedPrescription: any) => {
    Logger.debug("Changed selected nestable to", nestedPrescription);
    setSelectedNestable(nestedPrescription);
  };

  const onMenuClick = async (e: any) => {
    let selectedMenu = e.target.dataset.id;

    switch (selectedMenu) {
      case "add":
        addNewActivity();
        break;
      case "addnestedprescription":
        setShowAddNested(true);
        break;
      case "new":
        if (unsavedChanges) {
          setShowNewPrescriptionSave(true);
        } else {
          setShowNewPrescription(true);
        }
        break;
      case "saveas":
        if (!auth.email) {
          toast.error(t("msg_login_required_for_save"), {
            className: "!bg-red-500 !text-white",
          });
          break;
        }
        setShowSavePrescriptionAs(true);
        break;
      case "save":
        if (!auth.email) {
          saveDraft();
          toast.error(t("msg_login_required_for_save"), {
            className: "!bg-red-500 !text-white",
          });
          break;
        }
        if (!presName) {
          callMenu("saveas");
          break;
        }

        if (presOwner && presOwner !== auth.email) {
          setShowNotOwnerSaveCopy(true);
          break;
        }

        let finalMessage = "";

        try {
          let diagram = generateDiagram();

          let validationResult = validatePrescription(JSON.stringify(diagram));
          let isValid =
            validationResult.nodesReachable &&
            validationResult.oneOptionPerNode &&
            validationResult.openExits;

          Logger.debug("Diagram to be saved is valid?", diagram, isValid);

          let id = currentPrescriptionId;
          console.log(`onMenuClick current prescription id: ${id}`);

          if (!id) {
            // create new prescription
            let presHistoryId;
            if (sessionStorage.getItem("prescriptionHistoryID")) {
              presHistoryId = sessionStorage.getItem("prescriptionHistoryID");
              sessionStorage.removeItem("prescriptionHistoryID");
            }
            let newPrescription = {
              name: presName,
              diagramJSON: JSON.stringify(diagram),
              dirty: true,
              fiPrescriptionHistory: presHistoryId,
              valid: isValid,
            };
            sessionStorage.removeItem("newPrescriptionName");
            let newResponse = await retryFetch(
              config.API_BASE + "/api/prescriptions/",
              {
                method: "POST",
                headers: {
                  "Content-Type": "application/json",
                },
                credentials: "include",
                body: JSON.stringify(newPrescription),
              }
            );
            if (newResponse.ok) {
              const responseData = await newResponse.json();
              finalMessage = t("msg_save_successful");
              setUnsavedChanges(false);
              console.log(`onMenuClick create new prescription, setCurrentPrescriptionId ${responseData._id}`);
              setCurrentPrescriptionId(responseData._id);
            } else {
              finalMessage = t("msg_error_save_prescription");
            }
          } else {
            // update existing prescription

            let updateResponse = await retryFetch(
              `${config.API_BASE}/api/prescriptions/${id}`,
              {
                method: "PUT",
                headers: {
                  "Content-Type": "application/json",
                },
                credentials: "include",
                body: JSON.stringify({
                  diagram,
                  valid: isValid,
                }),
              }
            );
            if (updateResponse.ok) {
              finalMessage = t("msg_save_successful");
              setUnsavedChanges(false);
            } else {
              finalMessage = t("msg_error_save_activity");
            }
          }
        } catch (e) {
          finalMessage = t("msg_error_save_activity");
        }
        if (saveIntentAssignMessage) {
          setSaveIntentAssignMessage(false);

          if (finalMessage === t("msg_save_successful")) {
            toast.success(t("msg_engagement_assigned"));
          } else {
            toast.error(t("msg_engagement_assigned_but_save_failed"), {
              className: "!bg-red-500 !text-white",
            });
          }
        } else {
          toast.success(finalMessage);
        }
        break;
      case "validate":
        let presJSON = JSON.stringify(generateDiagram());

        let validationResult = validatePrescription(presJSON);

        showValidationBox(selectedMenu, "pure", true, validationResult);

        break;
      case "preview":
        let pid = currentPrescriptionId;
        if (!pid || unsavedChanges || (!auth.email && !presName)) {
          saveDraft();
        }
        // TODO: create a preview page dedicated to that, or maybe use run activity with a setting not to save any of the state at all
        //navigate("/preview");
        // window.open("/preview", "_blank");
        navigate(`/design?wid=${pid}`, { state: { background: location, from: location, forward: location } });
        break;
      case "run":
        if (currentPrescriptionId && !unsavedChanges) {
          sessionStorage.removeItem("draftprescription");

          let runPresJSON = JSON.stringify(generateDiagram());

          let runValidationResult = validatePrescription(runPresJSON);

          let isValid =
            runValidationResult.nodesReachable &&
            runValidationResult.oneOptionPerNode &&
            runValidationResult.openExits;

          showValidationBox(selectedMenu, "run", isValid, runValidationResult);
        } else {
          toast.error(t("msg_save_before_run"), {
            className: "!bg-red-500 !text-white",
          });
        }
        break;
      case "share":
        if (!auth.email) {
          toast.error(t("msg_login_required_for_share"), {
            className: "!bg-red-500 !text-white",
          });
          break;
        }
        let presid = currentPrescriptionId;
        if (!presid || unsavedChanges) {
          toast.error(t("msg_save_before_share"), {
            className: "!bg-red-500 !text-white",
          });
        } else {
          let sharePresJSON = JSON.stringify(generateDiagram());

          let shareValidationResult = validatePrescription(sharePresJSON);

          let shareIsValid =
            shareValidationResult.nodesReachable &&
            shareValidationResult.oneOptionPerNode &&
            shareValidationResult.openExits;

          showValidationBox(
            selectedMenu,
            "share",
            shareIsValid,
            shareValidationResult
          );
        }
        break;
      case "manage_permissions":
        if (!auth.email) {
          toast.error(t("msg_login_required_for_save"), {
            className: "!bg-red-500 !text-white",
          });
          break;
        }
        setShowPermissionDialog(true);
        break;
      case "zoomin":
        flow.zoomIn();
        break;
      case "zoomout":
        flow.zoomOut();
        break;
      case "fittoview":
        flow.fitView();
        break;
      default: {
      }
    }
  };

  async function loadPrescriptionFromHistory(id: string) {
    let responseHistory = await retryFetch(
      `${config.API_BASE}/api/fullprescriptionhistory/${id}`,
      {
        method: "GET",
        credentials: "include",
      }
    );

    if (responseHistory.ok) {
      let presResponse = await responseHistory.json();

      setPresName(presResponse.fiPrescription?.name);
      setInitialPresName(presResponse.fiPrescription?.name);
      setPresOwner(presResponse.fiPrescription?.fiUser?.email);

      return presResponse.diagramJSON;
    }
    return null;
  }

  const initReactFlow = (unsavedGraph: any) => {

    const findInQuery = () => {
      const searchParams = new URLSearchParams(location.search);
      return searchParams.get("wid"); // null if not defined
    };
    const findInStorage = () => {
      return sessionStorage.getItem("prescriptionID");
    };
    const selectedPrescriptionId = findInQuery() ?? findInStorage() ?? "";
    console.log(`initReactFlow selectedPrescriptionId ${selectedPrescriptionId}`);
    setCurrentPrescriptionId(selectedPrescriptionId);

    sessionStorage.removeItem("processID");
    sessionStorage.removeItem("nextActivity");

    let isRTL = document.body.dir === "rtl";

    Logger.debug("useEffect[] is running in RTL mode? " + isRTL);

    // load model from database
    const loadPrescription = async (id: string | undefined) => {
      let restString = "";

      if (id) {
        removeDraft();
        console.log(`loadPrescription from id ${id}`);
        setLoading(true);

        let response = await retryFetch(
          `${config.API_BASE}/api/prescriptions/${id}`,
          {
            method: "GET",
            credentials: "include",
          }
        );
        if (response.ok) {
          let pres = await response.json();

          setPresName(pres.name);
          setInitialPresName(pres.name);
          setPresOwner(pres.fiUser?.email);

          restString = pres.diagramJSON;

          Logger.debug(restString);
        } else {
          console.log(`loadPrescription from id FAILED`);
        }
      } else {
        // TODO: remove or rewrite this because it's the way preview and run activity tell design mode which one to edit, but we need to switch to using query parameters instead with the 'from' and 'forward' location states, end make "edit activity" a menu item in those modes for authors instead of relying on these local storage keys to exist
        const historyId = sessionStorage.getItem("prescriptionHistoryID");
        if (historyId) {
          console.log(`loadPrescription from prescriptionHistoryID: ${historyId}`);
          setLoading(true);
          restString = await loadPrescriptionFromHistory(historyId);
        }
      }

      if (restString) {
        await setPrescription(restString);
      }

      setLoading(false);

      // is user not-logged-in and did they just run a process? => Login in or register and then assign user to process
      if (sessionStorage.getItem("runProcessID")) {
        if (!auth.email) {
          setShowLoginRegister(true);
        } else {
          let response = await retryFetch(
            `${config.API_BASE}/api/processes/setuser/${sessionStorage.getItem(
              "runProcessID"
            )}/${auth.email}`,
            {
              method: "PUT",
              credentials: "include",
            }
          );
          if (response.ok) {
            let newName = presName + " copy " + Date.now();
            setPresName(newName);
            setInitialPresName(newName);
            setPresOwner(auth.email);
            sessionStorage.removeItem("prescriptionID");
            // sessionStorage.removeItem("prescriptionHistoryID");
            setSaveIntent(true);
            setSaveIntentAssignMessage(true);
            sessionStorage.removeItem("runProcessID");
          }
        }
      }
    };

    loadPrescription(selectedPrescriptionId);
  };

  useEffectOnce(() => {
    initReactFlow(null);

    if (
      document.body.dir !==
      LANGUAGES[prescriptionLanguage as keyof typeof LANGUAGES].dir
    ) {
      Logger.debug(
        "Prescription direction when starting DesignView is different from global direction ... need to initially transpose?"
      );
    }

    let directionUnsubscribe = directionService
      .getDirectionChange()
      .subscribe(() => {
        Logger.debug("Direction change detected in DesignView");
        // are there any unsaved changes? Keep them!
        if (unsavedChangesRef.current) saveDraft();
        setDesignDirection(document.body.dir);
        setNodes((prevNodes: Node[]) => {
          if (prevNodes.length) {
            setTransposable(false);

            Logger.debug(
              "TRANSPOSE called from direction change in DesignView"
            );
          }
          return transpose(prevNodes);
        });
        setEdges((prevEdges: Edge[]) => {
          let cloned = [...prevEdges];
          for (let i = 0; i < cloned.length; i++) {
            let clonedEdge = { ...cloned[i] };
            cloned[i] = clonedEdge;
          }
          return cloned;
        });
        setNodesDueSelecting(true);
      });

    return () => {
      directionUnsubscribe.unsubscribe();
    };
  });

  const onButton = async (result: any) => {
    Logger.debug("MessageBox button was clicked");
    Logger.debug(result);
    if (result.btnId === "run") {
      Logger.debug(
        "Validation successful ... now running",
        currentPrescriptionId,
        unsavedChanges
      );

      await runPrescription(currentPrescriptionId);
    } else if (result.btnId === "share") {
      let presid = currentPrescriptionId;
      let response = await retryFetch(
        `${config.API_BASE}/api/deeplinks/${presid}`,
        {
          method: "POST",
        }
      );
      let newDeeplink = await response.json();
      if (window.isSecureContext) {
        navigator.clipboard.writeText(
          `${window.location.origin}/preview?id=${newDeeplink.id}`
        );
        toast.success(t("msg_link_copied_to_clipboard"));
      } else {
        toast.error(t("msg_error_clipboard_insecure"), {
          className: "!bg-red-500 !text-white",
        });
      }
    }
  };

  const removeExtraProperties = (oldNodes: any) => {
    let cloned = [...oldNodes];
    for (let node of cloned) {
      node.width = undefined;
      node.height = undefined;
      node.selected = undefined;
      node.positionAbsolute = undefined;
      node.dragging = undefined;
      node.position.x = Math.floor(node.position.x);
      node.position.y = Math.floor(node.position.y);
    }
    return cloned;
  };

  const findOpenExitHandles = () => {
    // check all nodes for option handles that are not connected with an edge

    let result = [];
    for (let node of nodes) {
      for (let idx = 0; idx < data[node.id].options.length; idx++) {
        if (
          !edges.find((edge: any) => edge.sourceHandle === `${node.id}-${idx}`)
        ) {
          let newOpenExitHandle = {
            nodeLabel: data[node.id].label[prescriptionLanguage],
            optionLabel: data[node.id].options[idx].text[prescriptionLanguage],
            optionValue: data[node.id].options[idx].value,
            handleId: `${node.id}-${idx}`,
          };
          result.push(newOpenExitHandle);
        }
      }
    }
    return result;
  };

  useEffect(() => {
    if (nodesDueSelecting) {
      setTimeout(() => {
        Logger.debug("useEffect: nodes due selecting!");

        // Workaround: Simulate selecting every node
        let changes = [];
        for (let node of nodes) {
          changes.push({
            id: node.id,
            type: "select",
            selected: true,
          });
        }
        onNodesChange(changes);
        setNodesDueSelecting(false);
        setNodesDueUnSelecting(true);
      }, 10);
    } else if (nodesDueUnSelecting) {
      setTimeout(() => {
        Logger.debug("useEffect: nodes due unselecting!");

        // Workaround: Simulate unselecting every node
        let changes = [];
        for (let node of nodes) {
          changes.push({
            id: node.id,
            type: "select",
            selected: false,
          });
        }
        onNodesChange(changes);
        setNodesDueUnSelecting(false);

        highlightInfoRef.current = {
          ...highlightInfoRef.current,
          type: HIGHLIGHT_TYPE.NONE,
        };
      }, 10);
    }
  }, [nodes, nodesDueSelecting, nodesDueUnSelecting]);

  const showValidationBox = (
    selectedMenu: any,
    modus: any, // pure, run, share
    isValid: any,
    validationResult: any
  ) => {
    openMessageBox(
      t("validation_result"),
      selectedMenu,
      [
        isValid
          ? modus === "pure"
            ? { id: "ok", text: t("ok") }
            : modus === "run"
            ? { id: "run", text: t("run") }
            : { id: "share", text: t("share") }
          : {
              id: "cancel",
              text: t("cancel"),
            },
      ],
      [],
      <div className="msgtext">
        <div>
          {validationResult.nodesReachable ? (
            <FontAwesomeIcon
              icon={solid("check")}
              style={{ color: "lightgreen" }}
            />
          ) : (
            <FontAwesomeIcon
              icon={solid("close")}
              style={{ color: "red", padding: "0 4px 0 2px" }}
            />
          )}{" "}
          {t("every_step_reachable")}
        </div>
        <div>
          {validationResult.oneOptionPerNode ? (
            <FontAwesomeIcon
              icon={solid("check")}
              style={{ color: "lightgreen" }}
            />
          ) : (
            <FontAwesomeIcon
              icon={solid("close")}
              style={{ color: "red", padding: "0 4px 0 2px" }}
            />
          )}{" "}
          {t("every_step_one_option")}
        </div>
        <div>
          {validationResult.openExits ? (
            <FontAwesomeIcon
              icon={solid("check")}
              style={{ color: "lightgreen" }}
            />
          ) : (
            <FontAwesomeIcon
              icon={solid("close")}
              style={{ color: "red", padding: "0 4px 0 2px" }}
            />
          )}{" "}
          {t("open_exits_available")}
        </div>
        {isValid || modus === "pure" ? (
          <></>
        ) : modus === "run" ? (
          <div className="ptop">
            <b>{t("activity_cannot_run")}</b>
          </div>
        ) : (
          <div className="ptop">
            <b>{t("activity_cannot_be_shared")}</b>
          </div>
        )}
      </div>
    );
  };

  const initiateTranspose = () => {
    setNodes((prevNodes: any) => {
      if (prevNodes.length) Logger.debug("TRANSPOSE from initiateTranspose");
      return transpose(prevNodes);
    });
    setEdges((prevEdges: any) => {
      let cloned = [...prevEdges];
      for (let i = 0; i < cloned.length; i++) {
        let clonedEdge = { ...cloned[i] };
        cloned[i] = clonedEdge;
      }
      return cloned;
    });
    setNodesDueSelecting(true);
    setTransposable(false);
  };

  const handleMapEntryChange = (mapIdx: any, val: any) => {
    setSelectedMapping((oldMapping: any) => {
      let newMapping = structuredClone(oldMapping);
      newMapping[mapIdx] = val;
      return newMapping;
    });
  };

  const handlePaneClick = () => {
    setPaneWasClicked(true);
  };

  const handleLoginClick = async () => {
    saveDraft();
    navigate("/login", {
      state: { from: location },
    });
  };

  return (
    <div className="flexArea h-full">
      {!auth.email && (
        <div className="fixed inset-0 z-50 backdrop-blur-sm bg-white/30 flex items-center justify-center">
          <div className="bg-white/70 rounded-xl shadow-xl p-6 max-w-md text-center">
            <h2 className="text-xl font-semibold mb-4 text-gray-800">
              You&apos;ve been signed out
            </h2>
            <p className="text-gray-600 mb-6">
              Your session has expired. Please log in again to continue where
              you left off.
            </p>
            <button
              onClick={handleLoginClick}
              className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition"
            >
              Log In
            </button>
          </div>
        </div>
      )}
      <DesignMenu
        onClick={(e: any) => onMenuClick(e)}
        initialPresName={initialPresName}
        setPresName={setPresName}
        // newPresName={newPresName}
        // setNewPresName={setNewPresName}
        unsavedChanges={unsavedChanges}
        setUnsavedChanges={setUnsavedChanges}
      />
      <WorkflowPermissionDialog
        open={showPermissionDialog}
        onOpenChange={setShowPermissionDialog}
        workflowId={currentPrescriptionId}
      ></WorkflowPermissionDialog>
      <div className="bg-secondary w-lvw leading-6 h-full">
        {transposable &&
        designDirection !==
          LANGUAGES[prescriptionLanguage as keyof typeof LANGUAGES].dir ? (
          <div style={{ textAlign: "center" }}>
            <FontAwesomeIcon
              icon={solid("arrows-left-right")}
              onClick={initiateTranspose}
              title={t("transpose")}
            />
          </div>
        ) : (
          <></>
        )}
        <ReactFlow
          nodes={nodes}
          edges={edges}
          onNodesChange={handleNodesChange}
          onEdgesChange={handleEdgesChange}
          onNodeDragStart={detectedUnsavedChanges}
          onNodesDelete={detectedUnsavedChanges}
          // onEdgeUpdate={detectedUnsavedChanges}
          onEdgesDelete={detectedUnsavedChanges}
          onConnect={onConnect}
          onPaneClick={handlePaneClick}
          deleteKeyCode={["Backspace", "Delete"]}
          nodeTypes={nodeTypes}
          edgeTypes={edgeTypes}
          dir="ltr"
          connectionLineType={ConnectionLineType.SmoothStep}
          fitView
        >
          <Background variant={BackgroundVariant.Lines} color="#c9c9c9" />
        </ReactFlow>

        {/* <pre>{JSON.stringify(flowContext, null, 2)}</pre> */}
      </div>
      <Dialog open={showModal} onOpenChange={setShowModal}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t("delete")}</DialogTitle>
          </DialogHeader>
          <p>{t("msg_del_option_has_connections")}</p>
          <div className="flex flex-row gap-2 mt-2">
            <Button
              className="inline-block"
              onClick={() => {
                setShowModal(false);
                handleRemoveOptionOk(selectedOption.nodeId, selectedOption.idx);
                setSelectedOption({});
                setUnsavedChanges(true);
              }}
              data-id={"yes"}
              key={0}
              variant={`default`}
            >
              {t("yes")}
            </Button>
            <Button
              className="inline-block"
              onClick={() => {
                setShowModal(false);
                setSelectedOption({});
              }}
              data-id={"no"}
              key={1}
              variant={`outline`}
            >
              {t("no")}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
      <Dialog open={showRemoveActivity} onOpenChange={setShowRemoveActivity}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t("delete")}</DialogTitle>
          </DialogHeader>
          <p>{t("msg_del_activity_has_connections")}</p>
          <div className="flex flex-row gap-2 mt-2">
            <Button
              className="inline-block"
              onClick={() => {
                setShowRemoveActivity(false);
                handleRemoveActivityOk();
                setUnsavedChanges(true);
              }}
              data-id={"yes"}
              key={0}
              variant={`default`}
            >
              {t("yes")}
            </Button>
            <Button
              className="inline-block"
              onClick={() => {
                setShowRemoveActivity(false);
              }}
              data-id={"no"}
              key={1}
              variant={`outline`}
            >
              {t("no")}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
      <Dialog open={showOptionEdit} onOpenChange={setShowOptionEdit}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t("change_option")}</DialogTitle>
          </DialogHeader>
          <div className="inputdiv">
            <label>
              {t("label_text") +
                " (" +
                t(
                  LANGUAGES[prescriptionLanguage as keyof typeof LANGUAGES]
                    .label
                ) +
                ")"}
            </label>
            <br />
            <input
              type="text"
              className="modalinputtext"
              value={
                currentOption.text
                  ? currentOption.text[prescriptionLanguage]
                  : ""
              }
              onChange={handleLabelTextInput}
              autoFocus
            />
            <FontAwesomeIcon
              icon={solid("globe")}
              style={
                designDirection === "ltr"
                  ? { marginLeft: "10px", cursor: "pointer" }
                  : { marginRight: "10px", cursor: "pointer" }
              }
              onClick={() => setShowOptionI18N(true)}
            />
          </div>
          <div className="flex flex-row gap-2 mt-2">
            <Button
              className="inline-block"
              onClick={() => {
                setShowOptionEdit(false);
                handleSaveOption(selectedOption.nodeId, selectedOption.idx);
                setSelectedOption({});
                setUnsavedChanges(true);
              }}
              data-id={"save"}
              key={0}
              variant={`default`}
            >
              {t("save")}
            </Button>
            <Button
              className="inline-block"
              onClick={() => {
                setShowOptionEdit(false);
                setSelectedOption({});
              }}
              data-id={"cancel"}
              key={1}
              variant={`outline`}
            >
              {t("cancel")}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
      <Dialog open={showOptionI18N} onOpenChange={setShowOptionI18N}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t("option_label_translations")}</DialogTitle>
          </DialogHeader>
          {Object.keys(LANGUAGES).map((language) => (
            <div
              className="inputdiv"
              dir={LANGUAGES[language as keyof typeof LANGUAGES].dir}
              key={language}
            >
              <label htmlFor={language}>
                {t(LANGUAGES[language as keyof typeof LANGUAGES].label)}
              </label>
              <br />
              <input
                type="text"
                className="modalinputtext"
                value={currentOption.text ? currentOption.text[language] : ""}
                onChange={(e) => {
                  setCurrentOption((prevCurrentOption: any) => {
                    let cloned = { ...prevCurrentOption };
                    cloned.text = { ...cloned.text };
                    cloned.text[language] = e.target.value;
                    return cloned;
                  });
                  setUnsavedChanges(true);
                }}
                id={language}
              />
            </div>
          ))}
          <div className="flex flex-row gap-2 mt-2">
            <Button
              className="inline-block"
              onClick={() => {
                setShowOptionI18N(false);
              }}
              data-id={"ok"}
              key={0}
              variant={`default`}
            >
              {t("ok")}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
      <Dialog
        open={showActivityEdit}
        onOpenChange={() => setShowActivityEdit(!showActivityEdit)}
      >
        <DialogContent className="lg:min-w-[950px] min-w-full">
          <EditActivity
            customData={data[selectedActivity]?.custom}
            onSave={(exported: any) => {
              setData((oldData: any) => {
                let newData = { ...oldData };
                if (!newData[selectedActivity]) {
                  newData[selectedActivity] = {};
                }
                newData[selectedActivity].custom = exported;

                // update all variable usages in flowContext
                setFlowContext((oldFlowContext: any) => {
                  let result = [];
                  for (let oldEntry of oldFlowContext) {
                    let newEntry = { ...oldEntry };
                    let found = false;
                    // go through all interpolation fields and check
                    for (let nodeId of Object.keys(data)) {
                      let node = data[nodeId];
                      if (node.custom) {
                        if (!Array.isArray(node.custom))
                          node.custom = convertOldCustomToNew(node.custom);
                        for (let currentField of node.custom) {
                          if (
                            (currentField.type === "interpolation" ||
                              currentField.type === "aiprompt") &&
                            currentField.content.includes(`{${newEntry.key}}`)
                          ) {
                            found = true;
                            break;
                          }
                        }
                      }
                    }
                    newEntry.used = found;
                    result.push(newEntry);
                  }
                  updateContract(result);
                  return result;
                });
                return newData;
              });
              setShowActivityEdit(false);
              setSelectedActivity("");
            }}
            onCancel={() => {
              setShowActivityEdit(false);
              setSelectedActivity("");
            }}
          />
        </DialogContent>
      </Dialog>
      <Dialog open={showDataItemMapping} onOpenChange={setShowDataItemMapping}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t("contract_data_item_mapping")}</DialogTitle>
          </DialogHeader>
          <div className="flex">
            <div className="flex-1 font-bold">{t("outer_flow_data")}</div>
            <div className="flex-none mr-3 ml-3">&nbsp;</div>
            <div className="flex-1 font-bold">{t("contract_data")}</div>
          </div>
          {dataItemMapping.map((mapEntry: any, mapIdx: any) => (
            <div className="flex">
              <div className="flex-1">
                <Select
                  onValueChange={(val) => handleMapEntryChange(mapIdx, val)}
                  value={selectedMapping[mapIdx]}
                >
                  <SelectTrigger className="max-w-[95%]">
                    <SelectValue placeholder={selectedMapping[mapIdx]} />
                  </SelectTrigger>
                  <SelectContent>
                    {selectableContext
                      .filter((entry: any) => entry.type === mapEntry.type)
                      .map((variable: any) => (
                        <SelectItem value={variable.key}>
                          {variable.description}
                        </SelectItem>
                      ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="flex-none mr-2 mt-2">
                {designDirection === "ltr" ? <ArrowRight /> : <ArrowLeft />}
              </div>
              <div className="flex-1 border-2 rounded p-1 pl-2">
                {mapEntry.description}
              </div>
            </div>
          ))}
          {/* {JSON.stringify(selectableContext, null, 2)} */}
          {/* {JSON.stringify(selectedMapping, null, 2)} */}
          <div className="flex flex-row gap-2 mt-2">
            <Button
              className="inline-block"
              onClick={() => {
                // save mapping to data[nodeId].dataitemmapping
                let mappingToStore = structuredClone(
                  data[mappingNodeId].dataitemmapping
                );
                for (let i = 0; i < selectedMapping.length; i++) {
                  let mappedOuterKey = selectedMapping[i];
                  if (mappedOuterKey) {
                    let flowEntry = flowContext.find(
                      (entry: any) => entry.key === mappedOuterKey
                    );
                    mappingToStore[i].mappedToOuter = {
                      key: flowEntry.key,
                      description: flowEntry.description,
                    };
                  } else {
                  }
                }
                let nodeIdToStore = mappingNodeId;

                setData((oldData: any) => {
                  let newData = structuredClone(oldData);

                  newData[nodeIdToStore].dataitemmapping = mappingToStore;

                  return newData;
                });

                setShowDataItemMapping(false);
                setDataItemMapping([]);
                setSelectableContext([]);
                setSelectedMapping([]);
                setSelectableContext([]);
                setMappingNodeId("");
              }}
              data-id={"ok"}
              key={0}
              variant={`default`}
            >
              {t("ok")}
            </Button>
            <Button
              className="inline-block"
              onClick={() => {
                setShowDataItemMapping(false);
                setDataItemMapping([]);
                setSelectableContext([]);
                setSelectedMapping([]);
                setSelectableContext([]);
                setMappingNodeId("");
              }}
              data-id={"cancel"}
              key={1}
              variant={`outline`}
            >
              {t("cancel")}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
      <Dialog open={showAddNested} onOpenChange={setShowAddNested}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t("select_activity_to_add")}</DialogTitle>
          </DialogHeader>
          <ComboBox
            defaultValue=""
            baseURL={`${config.API_BASE}/api/dynamic/${auth.email}`}
            apiProp="prescriptions"
            onChange={handleChange}
            setIsComboboxValid={setIsComboboxValid}
            sortBy="name"
            limit={10}
            labelProp="name"
            valueProp="_id"
            debounceTimerMs={300}
            exclude={currentPrescriptionId}
          />
          <div className="flex flex-row gap-2 mt-2">
            <Button
              className="inline-block"
              onClick={() => {
                // add nested prescription
                Logger.debug("Selected prescription is", selectedNestable);
                addNestedPrescription(selectedNestable);
                setShowAddNested(false);
                setIsComboboxValid(false);
              }}
              data-id={"add"}
              key={0}
              variant={`default`}
              disabled={!isComboboxValid}
            >
              {t("add")}
            </Button>
            <Button
              className="inline-block"
              onClick={() => {
                setShowAddNested(false);
                setIsComboboxValid(false);
              }}
              data-id={"cancel"}
              key={1}
              variant={`outline`}
            >
              {t("cancel")}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
      <Dialog
        open={showNewPrescriptionSave}
        onOpenChange={setShowNewPrescriptionSave}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t("new_activity")}</DialogTitle>
          </DialogHeader>
          <label htmlFor="newName">{t("name")}</label>
          <input
            className="border-2 rounded pt-1 pl-2 pb-1"
            type="text"
            value={presNameNew}
            onChange={(e) => setPresNameNew(e.target.value)}
            id="newName"
          />
          <p>{t("msg_save_before_new")}</p>
          <div className="flex flex-row gap-2 mt-2">
            <Button
              className="inline-block"
              onClick={() => {
                setContract([]);
                setFlowContext([]);
                setSaveBeforeNewIntent(presNameNew);
                setUnsavedChanges(false);
                setShowNewPrescriptionSave(false);
                setPresNameNew("");
              }}
              data-id={"save"}
              key={0}
              variant={`default`}
              disabled={!presNameNew}
            >
              {t("save")}
            </Button>
            <Button
              className="inline-block"
              onClick={() => {
                sessionStorage.removeItem("prescriptionID");
                setPresName(presNameNew);
                setInitialPresName(presNameNew);
                clearReactFlow();
                setContract([]);
                setFlowContext([]);
                setUnsavedChanges(false);
                setShowNewPrescriptionSave(false);
                setPresNameNew("");
              }}
              data-id={"dontsave"}
              key={1}
              variant={`outline`}
              disabled={!presNameNew}
            >
              {t("dont_save")}
            </Button>
            <Button
              className="inline-block"
              onClick={() => {
                setShowNewPrescriptionSave(false);
                setPresNameNew("");
              }}
              data-id={"cancel"}
              key={1}
              variant={`outline`}
            >
              {t("cancel")}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
      <Dialog open={showNewPrescription} onOpenChange={setShowNewPrescription}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t("new_activity")}</DialogTitle>
          </DialogHeader>
          <label htmlFor="newName">{t("name")}</label>
          <input
            className="border-2 rounded pt-1 pl-2 pb-1"
            type="text"
            value={presNameNew}
            onChange={(e) => setPresNameNew(e.target.value)}
            id="newName"
          />
          <div className="flex flex-row gap-2 mt-2">
            <Button
              className="inline-block"
              onClick={() => {
                sessionStorage.removeItem("prescriptionID");
                setPresName(presNameNew);
                setInitialPresName(presNameNew);
                setContract([]);
                setFlowContext([]);
                clearReactFlow();
                setShowNewPrescription(false);
                setPresNameNew("");
              }}
              data-id={"ok"}
              key={0}
              variant={`default`}
              disabled={!presNameNew}
            >
              {t("ok")}
            </Button>
            <Button
              className="inline-block"
              onClick={() => {
                setShowNewPrescription(false);
                setPresNameNew("");
              }}
              data-id={"cancel"}
              key={1}
              variant={`outline`}
            >
              {t("cancel")}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
      <Dialog
        open={showSavePrescriptionAs}
        onOpenChange={setShowSavePrescriptionAs}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t("save_activity_as")}</DialogTitle>
          </DialogHeader>
          <label htmlFor="newName">{t("name")}</label>
          <input
            className="border-2 rounded pt-1 pl-2 pb-1"
            type="text"
            value={presNameNew}
            onChange={(e) => setPresNameNew(e.target.value)}
            id="newName"
          />
          <div className="flex flex-row gap-2 mt-2">
            <Button
              className="inline-block"
              onClick={() => {
                sessionStorage.removeItem("prescriptionID");
                setPresName(presNameNew);
                setInitialPresName(presNameNew);
                setSaveIntent(true);
                setPresNameNew("");
                setShowSavePrescriptionAs(false);
              }}
              data-id={"ok"}
              key={0}
              variant={`default`}
              disabled={!presNameNew}
            >
              {t("ok")}
            </Button>
            <Button
              className="inline-block"
              onClick={() => {
                setPresNameNew("");
                setShowSavePrescriptionAs(false);
              }}
              data-id={"cancel"}
              key={1}
              variant={`outline`}
            >
              {t("cancel")}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
      <Dialog
        open={showNotOwnerSaveCopy}
        onOpenChange={setShowNotOwnerSaveCopy}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t("question_not_owner_save_copy")}</DialogTitle>
          </DialogHeader>
          <div className="flex flex-row gap-2 mt-2">
            <Button
              className="inline-block"
              onClick={() => {
                let newName = presName + " copy " + Date.now();
                setPresName(newName);
                setInitialPresName(newName);
                setPresOwner(auth.email || "");
                sessionStorage.removeItem("prescriptionID");
                setSaveIntent(true);
                setShowNotOwnerSaveCopy(false);
              }}
              data-id={"yes"}
              key={0}
              variant={`default`}
            >
              {t("yes")}
            </Button>
            <Button
              className="inline-block"
              onClick={() => {
                setShowNotOwnerSaveCopy(false);
              }}
              data-id={"no"}
              key={1}
              variant={`outline`}
            >
              {t("no")}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
      <Dialog open={showLoginRegister} onOpenChange={setShowLoginRegister}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t("question_login_register")}</DialogTitle>
          </DialogHeader>
          <div className="flex flex-row gap-2 mt-2">
            <Button
              className="inline-block"
              onClick={() => {
                setShowLoginRegister(false);

                // login/register and come back to DesignView afterwards!
                navigate("/login", {
                  state: { from: location },
                });
              }}
              data-id={"yes"}
              key={0}
              variant={`default`}
            >
              {t("yes")}
            </Button>
            <Button
              className="inline-block"
              onClick={() => {
                setShowLoginRegister(false);
                sessionStorage.removeItem("runProcessID");
                sessionStorage.removeItem("prescriptionHistoryID");
              }}
              data-id={"no"}
              key={1}
              variant={`outline`}
            >
              {t("no")}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
      {showMessageBox ? (
        <MessageBox
          onButton={onButton}
          buttons={msgBoxButtons}
          text={msgBoxTitle}
          inputs={msgBoxInputs}
          id={msgBoxId}
          moreJSX={msgBoxMoreJSX}
          onClose={() => setShowMessageBox(false)}
        />
      ) : (
        ""
      )}
      {isLoading ? (
        <div className="loadingcenter">
          <img
            src={process.env.PUBLIC_URL + "/assets/images/loading.gif"}
            alt=""
          />
        </div>
      ) : (
        ""
      )}
      {background && <Preview />}
    </div>
  );
};

export default DesignView;
